import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DynamicFormField from 'src/shared/components/form/dynamicFormField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { getFormSettingPreview } from 'src/shared/services/memberFormSettingsService';
import { FormItemResponse } from 'src/modules/member/models/formMemberResponse';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';

type OnboardingStepFormsProps = {
  onNavigateAway?: () => void;
};

export const OnboardingStepForms = ({ onNavigateAway }: OnboardingStepFormsProps) => {
  const { t } = useTranslation(['onboarding']);
  const isMounted = useIsMounted();
  const [previewItems, setPreviewItems] = useState<FormItemResponse[]>([]);

  const fetchPreview = useCallback(async () => {
    try {
      const res = await getFormSettingPreview('member');
      if (isMounted()) {
        const hiddenKeys = new Set(['countyAss_id', 'localAss_id']);
        setPreviewItems((res.data.items || []).filter((item: FormItemResponse) => !hiddenKeys.has(item.formKey)));
      }
    } catch (e) {
      ConsoleHelper.log(e);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const previewFormMethods = useForm({
    defaultValues: { items: previewItems.map((item) => ({ value: item.value || item.formValue || '' })) },
  });

  const previewRows = previewItems.reduce((acc, item) => {
    const r = item.formSetting.row;
    if (!acc[r]) acc[r] = [];
    acc[r].push(item);
    return acc;
  }, {} as Record<number, FormItemResponse[]>);
  Object.values(previewRows).forEach((rowItems) => rowItems.sort((a, b) => a.formSetting.col - b.formSetting.col));

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.forms.description')}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.forms.hint')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding:stepContent.forms.previewTitle')}
          </Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 2, border: 1, borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
            <Alert severity='warning' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.forms.editHint')}{' '}
              <Button
                variant='text'
                size='small'
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                }}
                onClick={() => {
                  if (onNavigateAway) onNavigateAway();
                }}
              >
                {t('onboarding:stepContent.forms.editLink')}
              </Button>
              . {t('onboarding:stepContent.forms.editHintSuffix')}
            </Alert>
            <FormProvider {...previewFormMethods}>
              <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, '& .MuiInputBase-root': { bgcolor: '#fff' } }}>
                {Object.entries(previewRows).map(([row, rowItems]) => (
                  <Box sx={{ mb: 1 }} key={row}>
                    <GridWrapper>
                      {rowItems.map((item) => {
                        const idx = previewItems.indexOf(item);
                        const safeItem = { ...item, value: item.value ?? item.formValue ?? '' };
                        return (
                          <DynamicFormField key={`${item.formKey}-${item.formSetting.row}-${item.formSetting.col}`} formItem={safeItem} index={idx} />
                        );
                      })}
                    </GridWrapper>
                  </Box>
                ))}
                {previewItems.length === 0 && (
                  <Typography variant='body2' color='text.secondary'>
                    {t('onboarding:stepContent.forms.noFields')}
                  </Typography>
                )}
              </Box>
            </FormProvider>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
